/**
 * Age rules behind the Election Commission's forms.
 *
 * Bangladesh issues an NID from 16, but the electoral roll starts at 18: a
 * 16- or 17-year-old can hold a card and still not be a voter. That is why a
 * birth date under 18 is worth a word of warning on the voter forms rather than
 * a hard block — the applicant may be filling the form ahead of a birthday, or
 * for the NID alone.
 *
 * @see https://www.tbsnews.net/bangladesh/citizens-can-now-get-nids-age-16-election-commission-1232296
 * @see https://www.dhakatribune.com/bangladesh/election/330910/election-commission-nid-not-enough-for-voting
 */

/** Minimum age to be listed as a voter. */
export const VOTER_MIN_AGE = 18;

/** Minimum age to be issued an NID. */
export const NID_MIN_AGE = 16;

/** Whole years completed on `on`, or null when the date makes no sense. */
export function ageOn(birth, on = new Date()) {
    if (!(birth instanceof Date) || Number.isNaN(birth.getTime())) return null;
    if (birth > on) return null;
    let age = on.getFullYear() - birth.getFullYear();
    const monthDiff = on.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && on.getDate() < birth.getDate())) age -= 1;
    return age;
}

/** The day this birth date reaches `age` — the 18th birthday, by default. */
export function birthdayAt(birth, age = VOTER_MIN_AGE) {
    if (!(birth instanceof Date) || Number.isNaN(birth.getTime())) return null;
    return new Date(birth.getFullYear() + age, birth.getMonth(), birth.getDate());
}

/**
 * What a picked birth date means for the forms.
 *
 * @returns {{ age: number, voterEligible: boolean, nidOnly: boolean, turnsVoterAgeOn: Date }|null}
 *   `nidOnly` is the 16–17 window: old enough for a card, not yet for the roll.
 */
export function voterAgeStatus(birth, on = new Date()) {
    const age = ageOn(birth, on);
    if (age == null) return null;
    return {
        age,
        voterEligible: age >= VOTER_MIN_AGE,
        nidOnly: age >= NID_MIN_AGE && age < VOTER_MIN_AGE,
        turnsVoterAgeOn: birthdayAt(birth, VOTER_MIN_AGE),
    };
}

/** True when the applicant would not be listed as a voter yet. */
export function isUnderVoterAge(birth, on = new Date()) {
    const age = ageOn(birth, on);
    return age != null && age < VOTER_MIN_AGE;
}
